/*
 * Store call selector. Any [data-store-call-open] element opens it.
 * The openers are plain tel: links, so without JS (or before this script
 * runs) they still dial the first store; here the click becomes the selector.
 */
class StoreCall extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;

    // One controller owns every listener, so a reconnect never duplicates them.
    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();
    const { signal } = this.abortController;
    this.trigger = null;

    document.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-store-call-open]');
      if (!opener) return;
      event.preventDefault();
      this.open(opener);
    }, { signal });

    // The dialog has no padding, so a click whose target is the dialog itself
    // landed on the backdrop. Closing never relies on the native "close"
    // event: some browsers do not fire it, so close() cleans up directly.
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog || event.target.closest('[data-store-call-close]')) {
        this.close();
      } else if (event.target.closest('.store-call__link')) {
        // No preventDefault: the tel: navigation proceeds.
        this.close();
      }
    }, { signal });
    this.dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.close();
    }, { signal });
    this.dialog.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
      }
    }, { signal });
    // Safety net: idempotent, so a real close event is harmless.
    this.dialog.addEventListener('close', () => this.onClose(), { signal });

    // Back/forward cache restore: never come back with the dialog or lock on.
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) this.close();
    }, { signal });

    // Crossing the 990px breakpoint would leak desktop positioning into the
    // mobile sheet (and the mobile lock into desktop): just close.
    window.matchMedia('(min-width: 990px)').addEventListener('change', () => {
      if (this.dialog.open) this.close();
    }, { signal });
  }

  disconnectedCallback() {
    if (this.abortController) this.abortController.abort();
    document.documentElement.classList.remove('store-call-lock');
  }

  open(trigger) {
    if (this.dialog.open) return;
    this.trigger = trigger;
    this.position(trigger);
    try {
      this.dialog.showModal();
    } catch (error) {
      // showModal failed: nothing opened, so nothing to lock or clean up.
      this.trigger = null;
      return;
    }
    // Lock only after the dialog really opened.
    if (window.matchMedia('(max-width: 989px)').matches) {
      document.documentElement.classList.add('store-call-lock');
    }
  }

  // Desktop: anchor the panel under the trigger. Mobile: CSS bottom sheet.
  position(trigger) {
    this.dialog.style.top = '';
    this.dialog.style.right = '';
    if (!window.matchMedia('(min-width: 990px)').matches) return;
    const rect = trigger.getBoundingClientRect();
    this.dialog.style.top = `${Math.max(8, Math.round(rect.bottom + 8))}px`;
    this.dialog.style.right = `${Math.max(8, Math.round(document.documentElement.clientWidth - rect.right))}px`;
  }

  close() {
    if (this.dialog.open) this.dialog.close();
    this.onClose();
  }

  // Idempotent: runs from close() and, when it fires, the native close event.
  onClose() {
    document.documentElement.classList.remove('store-call-lock');
    this.dialog.style.top = '';
    this.dialog.style.right = '';
    const trigger = this.trigger;
    this.trigger = null;
    if (trigger && trigger.isConnected) trigger.focus();
  }
}
customElements.define('store-call', StoreCall);
